import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../primitives/button';
import { cn } from '../lib/cn';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  labels?: { previous: string; next: string; showing: (from: number, to: number, total: number) => string };
  className?: string;
}

const defaultLabels = {
  previous: 'Previous',
  next: 'Next',
  showing: (from: number, to: number, total: number) => `Showing ${from}–${to} of ${total}`,
};

// First, last, current and its neighbours; gaps become "…"
function pageNumbers(page: number, pageCount: number): (number | 'gap')[] {
  const pages = [...new Set([1, pageCount, page - 1, page, page + 1])]
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);
  return pages.flatMap((p, i) => (i > 0 && p - pages[i - 1]! > 1 ? (['gap', p] as const) : [p]));
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  labels = defaultLabels,
  className,
}: PaginationProps) {
  if (total === 0) return null;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className={cn('flex flex-col-reverse items-center justify-between gap-3 sm:flex-row', className)}>
      <span className="text-sm text-muted-foreground tabular-nums">{labels.showing(from, to, total)}</span>
      {pageCount > 1 && (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            aria-label={labels.previous}
          >
            <ChevronLeft />
            <span className="hidden sm:inline">{labels.previous}</span>
          </Button>
          {pageNumbers(page, pageCount).map((p, i) =>
            p === 'gap' ? (
              <span key={`gap-${i}`} className="px-1 text-muted-foreground">
                …
              </span>
            ) : (
              <Button
                key={p}
                variant={p === page ? 'secondary' : 'ghost'}
                size="sm"
                className="min-w-8 px-2 tabular-nums"
                aria-current={p === page ? 'page' : undefined}
                onClick={() => onPageChange(p)}
              >
                {p}
              </Button>
            ),
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page === pageCount}
            aria-label={labels.next}
          >
            <span className="hidden sm:inline">{labels.next}</span>
            <ChevronRight />
          </Button>
        </nav>
      )}
    </div>
  );
}
